import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemButton,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as TransactionIcon,
  Settings as SettingsIcon,
  ShowChart as PortfolioIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  AdminPanelSettings as AdminIcon,
  EmojiEvents as EmojiEventsIcon,
  ListAlt as ListAltIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandLess,
  ExpandMore,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isRTL } from '../i18n';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { isVirtualPortfolioEnabled } from '../constants/featureFlags';
import {
  TRANSACTIONS_ENABLED,
  VIRTUAL_PORTFOLIO_ENABLED,
} from '../config/featureFlags';

// Helper function to check admin access
const isAdminUser = (email) => {
  return email === 'amos.shahar@gmail.com' || email === 'amos1.shahar1@gmail.com';
};

export default function MobileDrawer({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { navigateWithLang } = useLanguageNavigation();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { currentUser, logout } = useAuth();
  const rtl = isRTL();
  // Remove: const [portfolioExpanded, setPortfolioExpanded] = useState(false);

  // Build menu items based on authentication status
  const menuItems = [
    { 
      text: t('navigation.portfolio'), 
      icon: <WalletIcon />, 
      path: 'portfolio',
      hasSubItems: true, // Always show sub-items (at least Portfolio Overview)
      subItems: currentUser ? [
        { text: t('navigation.portfolioOverview'), path: 'portfolio' },
        { text: t('navigation.wallets'), path: 'wallets' },
        { text: t('navigation.manualHoldings'), path: 'manual-holdings' },
        { text: t('navigation.exitStrategies'), path: 'all-exit-strategies' },
        ...(TRANSACTIONS_ENABLED
          ? [{ text: t('navigation.walletTransactions'), path: 'wallet-transactions' }]
          : []),
      ] : [
        { text: t('navigation.portfolioOverview'), path: 'portfolio' },
      ],
      onClick: () => handleNavigation('portfolio')
    },
    ...((VIRTUAL_PORTFOLIO_ENABLED && isVirtualPortfolioEnabled(isAdminUser(currentUser?.email))) ? [
      { text: t('navigation.virtualPortfolio'), icon: <WalletIcon />, path: 'v-portfolio' }
    ] : []),
    ...(currentUser ? [
      { text: t('navigation.settings'), icon: <SettingsIcon />, path: 'settings' }
    ] : []),
  ];

      // Add admin menu item only for admin users
    if (isAdminUser(currentUser?.email)) {
    menuItems.push({ text: t('navigation.admin'), icon: <AdminIcon />, path: 'admin' });
  }

  const handleDrawerToggle = () => {
    onClose();
  };

  const handleNavigation = (path) => {
    navigateWithLang(path);
    onClose();
  };

  // Remove: const handlePortfolioClick = () => {
  // Remove:   setPortfolioExpanded(!portfolioExpanded);
  // Remove:   navigate('/portfolio');
  // Remove: };

  const handleLogout = async () => {
    await logout();
    if (isMobile) onClose();
  };

  const handleLogin = () => {
    navigateWithLang('');
    onClose();
  };

  // Helper function to check if a path is selected (accounting for language prefix)
  const isPathSelected = (path) => {
    const currentPath = location.pathname;
    // Remove language prefix if present
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, '');
    return pathWithoutLang === `/${path}` || pathWithoutLang === path;
  };

  // Helper function to check if current path starts with a given path
  const isPathStartingWith = (path) => {
    const currentPath = location.pathname;
    // Remove language prefix if present
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, '');
    return pathWithoutLang.startsWith(`/${path}`) || pathWithoutLang.startsWith(path);
  };

  const drawer = (
    <Box dir={rtl ? 'rtl' : 'ltr'} sx={{ width: 250, pt: 2, height: '100vh', minHeight: 0, display: 'flex', flexDirection: 'column', mt: { xs: '56px', sm: '64px' } }}>
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.hasSubItems ? (
              <>
                <ListItemButton
                  onClick={item.onClick}
                  selected={isPathStartingWith('portfolio') || isPathStartingWith('wallets') || isPathStartingWith('wallet-transactions') || isPathStartingWith('manual-holdings') || isPathStartingWith('all-exit-strategies')}
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    borderLeft: '3px solid',
                    borderLeftColor: 'primary.main',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{ fontWeight: 600 }}
                    sx={rtl ? { textAlign: 'right' } : {}} 
                  />
                </ListItemButton>
                {/* Always show subItems */}
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      onClick={() => handleNavigation(subItem.path)}
                      selected={isPathSelected(subItem.path)}
                      sx={{
                        pl: 4,
                        backgroundColor: 'rgba(25, 118, 210, 0.02)',
                        borderLeft: '2px solid',
                        borderLeftColor: 'primary.main',
                        ml: 1,
                        mr: 1,
                        borderRadius: 1,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          borderLeftColor: 'primary.main',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        },
                      }}
                    >
                      <ListItemText 
                        primary={subItem.text} 
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          fontWeight: isPathSelected(subItem.path) ? 600 : 400,
                        }}
                        sx={rtl ? { textAlign: 'right' } : {}} 
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            ) : (
              <ListItemButton
                onClick={item.onClick || (() => handleNavigation(item.path))}
                selected={isPathSelected(item.path)}
              >
                <ListItemIcon sx={{ color: 'primary.main' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} sx={rtl ? { textAlign: 'right' } : {}} />
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List>
        <ListItemButton onClick={() => handleNavigation('about')}>
          <ListItemIcon sx={{ color: 'text.secondary' }}>
            <InfoIcon />
          </ListItemIcon>
          <ListItemText primary={t('about.title')} primaryTypographyProps={{ color: 'text.secondary' }} sx={rtl ? { textAlign: 'right' } : {}} />
        </ListItemButton>
        {currentUser ? (
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon sx={{ color: 'text.primary' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('common.logout')} primaryTypographyProps={{ color: 'text.primary' }} sx={rtl ? { textAlign: 'right' } : {}} />
          </ListItemButton>
        ) : (
          <ListItemButton onClick={handleLogin}>
            <ListItemIcon sx={{ color: 'text.primary' }}>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary={t('common.signIn')} primaryTypographyProps={{ color: 'text.primary' }} sx={rtl ? { textAlign: 'right' } : {}} />
          </ListItemButton>
        )}
      </List>
    </Box>
  );

  return (
    <Drawer
      anchor={isRTL() ? 'right' : 'left'}
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      {drawer}
    </Drawer>
  );
} 